import { TextLink, TextLinkCaret } from "@vds/buttons";
import { Icon } from "@vds/icons";
import { Loader } from "@vds/loaders";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { BigPlayButton, ControlBar, ForwardControl, FullscreenToggle, PlaybackRateMenuButton, Player, ReplayControl } from "video-react";
import "video-react/dist/video-react.css";
import { getActivityLog } from "../../model/eventService";
import { showNotificationBanner } from "../../store/actions";
import { EventDeleteText } from "..//components/events/styles";
import { hasPermission } from "../utility/permissionUtils";
import { convertDateFormat, togglePlayPause } from "../utility/Utils";
import useDownload from "./useDownload";
import {EVENTS_INFO_CONSTANTS} from "../../global/constants/constants";

const VideoPlay = ({
  evidenceUrl,
  posterImage,
  evidenceId,
  mediaId,
  mediaStartTime,
  mediaEndTime,
  availability,
  hasMedia,
  isMediaDeleted,
  incidentId,
  videoLoader,
  cameraId,
  hasOnlyVideo,
  hasOnlyImage,
  hasVideoAndImage,
  videoDownloadError,
  isCaptureVideoEvidence,
  onFetchFreshUrl
}) => {
  const { cameraList } = useSelector((state) => state?.deviceReducer);
  const [playing, setPlaying] = useState(false);
  const [videoUrl, setVideoUrl] = useState(null);
  const [image, setImage] = useState(null);
  const [isVideoTransitioning, setIsVideoTransitioning] = useState(false);
  const prevEvidenceIdRef = useRef(null);
  const prevIncidentIdRef = useRef(null);
  const playerRef = useRef(null);
  const [tableLoading, setTableLoading] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [availableTill, setAvailableTill] = useState(null);
  const [cameraName, setCameraName] = useState(null);
  const [cameraObject, setCameraObject] = useState(null);
  const [isMuted, setMuted] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentTimeRef = useRef({
      seekTime: 0,
      duration: 0
    });
  const evidenceKey = mediaId || evidenceId;
  const handleStateChange = (state) => {
    setPlaying(!state.paused);
    const player = playerRef.current;
    if (player) {
      if (state.paused) {
        if (!isMuted) {
          player.muted = true;
          setMuted(true);
        }
      } else {
        player.muted = isMuted;
      }
    }
  };
  const { activeSession } = useSelector((state) => state.sessionReducer.activeSession);
  useEffect(() => {
    const player = playerRef.current;
    if (player) {
      const unsubscribe = player?.subscribeToStateChange(handleStateChange);
      return () => {
        if (unsubscribe && typeof unsubscribe === "function") unsubscribe();
      };
    }
  }, [videoUrl, isMuted]);

  // Handle video transition when evidence changes (works for both id and mediaId)
  useEffect(() => {
    if (evidenceKey && prevEvidenceIdRef?.current !== evidenceKey) {
      const player = playerRef?.current;
      try {
        player && player?.pause && player?.pause();
      } catch (e) {
        console.log("Getting following error while trying to pause video", e);
      }
      setIsVideoTransitioning(true);
      setVideoUrl(null);
      prevEvidenceIdRef.current = evidenceKey;
    }
  }, [evidenceKey]);

  // Also handle transition when the overall incidentId changes (stop old video immediately)
  useEffect(() => {
    if (incidentId && prevIncidentIdRef?.current !== incidentId) {
      const player = playerRef?.current;
      try {
        player && player?.pause && player?.pause();
      } catch (e) {
        console.log("Getting following error while trying to pause video", e);
      }
      setIsVideoTransitioning(true);
      setVideoUrl(null);
      prevIncidentIdRef.current = incidentId;
    }
  }, [incidentId]);

  const handleTogglePlayPause = () => {
    togglePlayPause(playing, playerRef, currentTimeRef, videoUrl);
  };
  useEffect(() => {
    let formattedPosterImage = posterImage;

    // Handle different image formats
    if (posterImage) {
      if (posterImage.startsWith("blob:")) {
        // Use blob URL directly
        formattedPosterImage = posterImage;
      } else if (posterImage.startsWith("data:image/")) {
        // Use data URL directly
        formattedPosterImage = posterImage;
      } else if (posterImage.startsWith("http")) {
        // Use HTTP URL directly
        formattedPosterImage = posterImage;
      } else {
        // Assume it's base64 encoded string without prefix
        formattedPosterImage = `data:image/png;base64,${posterImage}`;
      }
    }

    if (isMediaDeleted && hasMedia) {
      setImage(null);
    } else {
      if (hasOnlyImage) { 
        setImage(formattedPosterImage);
      }
    }
    if (evidenceUrl && evidenceUrl.length > 0 && prevEvidenceIdRef.current === evidenceKey) {
      setTableLoading(true);
      setVideoUrl(evidenceUrl);
      setIsVideoTransitioning(false);
      setPlaying(true);
      setImage(null); // Clear image to show video
    } else if(prevEvidenceIdRef?.current === evidenceKey) {
      setVideoUrl("");
      setTableLoading(false);
    }
  }, [evidenceUrl, posterImage]);
  const handleDownloadError = (errorMsg) => {
    dispatch(showNotificationBanner({
      showNotification: true,
      type: "Failed",
      title: errorMsg,
      priority: "critical"
    }));
  };
  const { isLoading, handleDownload } = useDownload(
    evidenceId,
    evidenceUrl,
    mediaId,
    handleDownloadError,
    onFetchFreshUrl
  );

  useEffect(() => {
    const { formatOnlyTime } = convertDateFormat(mediaStartTime);
    setStartTime(formatOnlyTime);
  }, [mediaStartTime, mediaEndTime]);

  useEffect(() => {
    const { formatOnlyTime } = convertDateFormat(mediaEndTime);
    setEndTime(formatOnlyTime);
  }, [mediaStartTime, mediaEndTime]);

  const action = "Video Downloaded";
  const activityLogApi = async (action, incidentId, activeSession) => {
    try {
      const response = await getActivityLog(action, incidentId, activeSession);
    } catch (e) {
      console.log("Getting following error while trying to call activitylog api", e);
    }
  };

  const showPermissionsBanner = () => {
    const notificationInfo = {
      type: "No Permission",
      priority: "critical",
      showNotification: true
    };
    dispatch(showNotificationBanner(notificationInfo));
  };

  useEffect(() => {
    if (cameraList && cameraList.length > 0) {
      const device = cameraList.find((device) => device?.id === cameraId);
      if (device) {
        setCameraName(device.name);
        setCameraObject(device);
      }
    }
  }, []);

  useEffect(() => {
    const { formatWithoutTime } = convertDateFormat(availability);
    setAvailableTill(formatWithoutTime);
  }, [availability]);

  return (
    <>
      <div className="video-player-container">
        <div className="video-player-body">
          {!isMediaDeleted && (isVideoTransitioning || !videoUrl) ? (
            // Show placeholder image while video is transitioning or loading
            <div
              className="video-transition-loader"
              style={{
                backgroundImage: image ? `url(${image})` : "none"
              }}
            >
              <div
                style={{
                  position: "absolute",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                {!isCaptureVideoEvidence && <Loader active={isVideoTransitioning || videoLoader} surface="dark" fullscreen={false} />}
              </div>
            </div>
          ) : (
            <Player className={"my-player"} ref={playerRef} poster={image} src={videoUrl} fluid={false} width={"100%"} height={550} autoPlay={true} playsInline preload="metadata" crossOrigin="anonymous">
              <BigPlayButton disabled />
              {videoLoader ? (
                <div className="video-loader">
                  <Loader active={true} surface="dark" fullscreen={false} />
                </div>
              ) : (
                <div className={`overlay ${playing ? "hidden" : ""}`} onClick={handleTogglePlayPause}>
                  {(hasOnlyVideo || hasVideoAndImage) &&
                    (!isMediaDeleted && !videoDownloadError ? (
                      <Icon name={playing ? "pause" : "play"} size="50px" color="#ffffff" />
                    ) : (
                      <div style={{ cursor: "not-allowed" }}>
                        <Icon name="no-video" size="50px" color="#ffffff" />
                      </div>
                    ))}
                </div>
              )}
              <ControlBar autoHide>
                <ReplayControl seconds={10} order={1.1} />
                <ForwardControl seconds={10} order={1.2} />
                <PlaybackRateMenuButton rates={[0.25, 0.75, 0.5, 1, 1.5, 2]} order={7.1} />
                <FullscreenToggle />
              </ControlBar>
            </Player>
          )}
        </div>
        {hasOnlyVideo && !isMediaDeleted ? (
          <div className="video-time-overlay">
            <div>{startTime}</div>
            <div>-</div>
            <div>{endTime}</div>
          </div>
        ) : (
          ""
        )}

        {(hasVideoAndImage || hasOnlyVideo) && !isMediaDeleted ? (          
          <div className="downloadtext-height">
            <TextLink
              type="standAlone"
              surface="light"
              disabled={isLoading}
              onClick={() => {
                if (!hasPermission("Evidence video", "Download")) {
                  showPermissionsBanner();
                } else {
                  handleDownload();
                  activityLogApi(action, incidentId, activeSession);
                }
              }}
            >
              {isLoading ? "Downloading..." : "Download video"}
            </TextLink>
            <div className="right-aligned">
              {cameraName?.length > 0 ? (
                <TextLinkCaret
                  surface="light"
                  iconPosition="right"
                  disabled={!cameraName}
                  onClick={() => {
                    sessionStorage.setItem("selectedCameraInfo", JSON.stringify({ data: cameraObject }));
                    navigate(`/cameraInfoPage?cameraId=${cameraObject?.id}`, {
                      state: {
                        data: cameraObject
                      }
                    });
                  }}
                >
                  {cameraName?.length > 0 ? cameraName : "Camera Unavailable"}
                </TextLinkCaret>
              ) : (
                ""
              )}
            </div>
          </div>
        ) : (
          ""
        )}

        {hasVideoAndImage && <div>
          {isMediaDeleted ? (
            <EventDeleteText>{EVENTS_INFO_CONSTANTS.EVENT_CREATED_MONTH_BACK(availableTill)}</EventDeleteText>
          ) : (
            <EventDeleteText>{EVENTS_INFO_CONSTANTS.EVENT_EVIDENT_REMOVAL_WARNING} {availableTill}.</EventDeleteText>
          )}
        </div>}
      </div>
    </>
  );
};

export default VideoPlay;
